import React, { useEffect, useState } from 'react';
import i18n from 'i18n-js';
import { StyleSheet, Text, View } from 'react-native';

import { scale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import Loading from '@components/loading';
import Svg from '@styles/icons/svg';
import { isPhone, bytesToView } from '@helpers';

import { Machine } from '@interfaces/machines';
import { modalFieldBg, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

export enum VersionType {
  FAVORITE = 'favoriteVersion',
  SPECIFIC = 'specificVersion',
}

const SendNCFileModal = ({
  C,
  close,
  sendNcFileToMachine,
  selectedMachine,
  selected,
  favorite,
  machines,
  NCFile,
  contentSize,
  versionType = VersionType.SPECIFIC,
}: {
  C: any;
  close: () => void;
  selected: any;
  NCFile: any;
  favorite: boolean;
  machines: Machine[];
  selectedMachine: Machine;
  sendNcFileToMachine: any;
  contentSize: number;
  versionType?: VersionType;
}) => {
  const sendingData = {
    NCFileName: NCFile.ncFileName,
    version: `${selected.sequenceNumber} ${favorite ? '(favorite)' : ''}`,
    toMachine: selectedMachine.fullName,
    map: selectedMachine.machineProperties.find((i) => i.propertyKey === 'SendNcFileDefaultFolder')?.propertyValue || '',
    size: contentSize ? bytesToView(contentSize) : '',
  };

  useEffect(() => {
    (async () => {
      const res = await sendNcFileToMachine({
        $type: versionType,
        machineId: selectedMachine.id,
        ncFileVersionId: selected.ncFileVersionId,
      });

      setIsSending(false);

      if (res?.errorMessage || res.errorType || res === 'timeout') {
        setError(res.errorMessage || res.errorType || 'Timeout');
      }

      setIsSending(false);
    })();
  }, []);

  const [isSending, setIsSending] = useState(true);
  const [error, setError] = useState(false);

  const contentConfig = {
    NCFileName: 'NC FILE',
    version: 'VERSION',
    toMachine: 'TO MACHINE',
    map: 'MAP',
    size: 'SIZE',
  };

  return (
    <View testID={TEST_IDS.sendToMachinePopup.sendModal}>
      <View
        style={{
          height: scale(46),
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingHorizontal: scale(17),
          alignItems: 'center',
        }}
      >
        <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Send NC File')}</Text>
        <TouchableElement onPress={close}>
          {({ boxStyle, pressHoverColor }) => (
            <View style={[boxStyle]}>
              <Text style={[font.Body, { color: C(pressHoverColor('System/Light/Blue')) }]}>{i18n.t(isSending ? 'Cancel' : 'Close')}</Text>
            </View>
          )}
        </TouchableElement>
      </View>
      <View
        style={{
          marginBottom: scale(30),
          backgroundColor: C(modalFieldBg),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          marginHorizontal: scale(15),
        }}
      >
        {Object.keys(contentConfig).map((lineTitle, index) => {
          return (
            <View
              style={[
                {
                  height: scale(38),
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: scale(15),
                },
                index !== Object.keys(contentConfig).length - 1 && {
                  borderBottomColor: C('Hairline/Light/No trans'),
                  borderBottomWidth: StyleSheet.hairlineWidth,
                },
              ]}
              key={lineTitle}
            >
              <View style={{ width: '30%' }}>
                <Text style={[font['Caption 2'], { color: C('Label/Light/Tertiary'), marginLeft: 'auto' }]}>{i18n.t(contentConfig[lineTitle])}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[font.Footnote, { color: C('Label/Light/Primary') }]}>{i18n.t(sendingData[lineTitle]) || '-'}</Text>
              </View>
            </View>
          );
        })}
      </View>
      <View style={{ paddingBottom: scale(15), alignItems: 'center', gap: scale(5), minHeight: scale(86) }}>
        {isSending ? (
          <>
            <Loading background="Background/Light/Panel" />
            <Text style={[font.Body, { color: C('Label/Light/Primary') }, isPhone && isSending && { marginTop: scale(15) }]}>
              {i18n.t('Sending...')}
            </Text>
          </>
        ) : error ? (
          <>
            <Svg icon="circledExclamationMark" width={scale(44)} height={scale(44)} />
            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Sending Failed')}</Text>
            <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t(error)}</Text>
          </>
        ) : (
          <>
            <Svg icon="circledCheck" width={scale(44)} height={scale(44)} />
            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Done')}</Text>
          </>
        )}
      </View>
    </View>
  );
};

export default SendNCFileModal;
